/**
 * SliceSpawnSystem - deterministic spawner for the vertical slice run.
 */

import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import { Barrier } from '../entities/Barrier';
import { EliteEnemy } from '../entities/EliteEnemy';
import { Enemy } from '../entities/Enemy';
import { Gate } from '../entities/Gate';
import {
  BarrierConfig,
  SliceEnemyConfig,
  SliceGateRowConfig,
  SliceLevelConfig,
} from '../levels/VerticalSliceData';
import { getGateLaneX, getLaneX } from '../levels/LevelData';
import { computeSliceDdaModifier, lockBarrierHpAtSpawn } from './sliceDda';
import { advanceProgressDistance, updateAndCompactEntities } from './spawn/entityLifecycle';

export interface DdaSnapshot {
  progress01: number;
  currentArmy: number;
  expectedArmy: number;
  multiplier: number;
}

export interface SliceSpawnSystemConfig {
  scene: Phaser.Scene;
  levelConfig: SliceLevelConfig;
  getScrollSpeed: () => number;
  getIsPaused: () => boolean;
  getCurrentArmySize: () => number;
  trackWidth?: number;
  trackPadding?: number;
}

export class SliceSpawnSystem {
  private readonly scene: Phaser.Scene;
  private readonly levelConfig: SliceLevelConfig;
  private readonly getScrollSpeed: () => number;
  private readonly getIsPaused: () => boolean;
  private readonly getCurrentArmySize: () => number;
  private readonly trackWidth: number;
  private readonly trackPadding: number;

  private progressDistance: number = 0;
  private isLevelComplete: boolean = false;

  private readonly barriersToSpawn: BarrierConfig[] = [];
  private readonly enemiesToSpawn: SliceEnemyConfig[] = [];
  private readonly gatesToSpawn: SliceGateRowConfig[] = [];

  private barrierSpawnIndex: number = 0;
  private enemySpawnIndex: number = 0;
  private gateSpawnIndex: number = 0;

  private readonly activeBarriers: Barrier[] = [];
  private readonly activeEnemies: Enemy[] = [];
  private readonly activeGates: Gate[] = [];

  private lastDdaAppliedAtSpawn: DdaSnapshot | null = null;

  private readonly events: Phaser.Events.EventEmitter;

  constructor(config: SliceSpawnSystemConfig) {
    this.scene = config.scene;
    this.levelConfig = config.levelConfig;
    this.getScrollSpeed = config.getScrollSpeed;
    this.getIsPaused = config.getIsPaused;
    this.getCurrentArmySize = config.getCurrentArmySize;
    this.trackWidth = config.trackWidth ?? GAME_WIDTH - 40;
    this.trackPadding = config.trackPadding ?? 20;

    this.events = new Phaser.Events.EventEmitter();

    for (const beat of this.levelConfig.beats) {
      this.barriersToSpawn.push(...beat.barriers);
      this.enemiesToSpawn.push(...beat.enemies);
      this.gatesToSpawn.push(...beat.gates);
    }

    this.barriersToSpawn.sort((a, b) => a.triggerDistance - b.triggerDistance);
    this.enemiesToSpawn.sort((a, b) => a.triggerDistance - b.triggerDistance);
    this.gatesToSpawn.sort((a, b) => a.triggerDistance - b.triggerDistance);
  }

  update(delta: number): void {
    if (this.getIsPaused() || this.isLevelComplete) return;

    this.progressDistance = advanceProgressDistance(this.progressDistance, this.getScrollSpeed(), delta);

    this.spawnPendingBarriers();
    this.spawnPendingEnemies();
    this.spawnPendingGates();

    this.updateActiveBarriers(delta);
    this.updateActiveEnemies(delta);
    this.updateActiveGates(delta);

    if (this.progressDistance >= this.levelConfig.trackLength) {
      this.isLevelComplete = true;
      this.events.emit('levelComplete');
    }
  }

  computeDdaModifier(progress01: number, currentArmy: number, expectedArmy: number): number {
    return computeSliceDdaModifier(progress01, currentArmy, expectedArmy);
  }

  spawnBarrierLocked(config: BarrierConfig, ddaModifier: number): Barrier {
    const finalHp = lockBarrierHpAtSpawn(config.baseHp, ddaModifier);
    const x = getLaneX(config.lane, this.trackWidth, this.trackPadding);
    const y = -Barrier.HEIGHT;

    const barrier = new Barrier(this.scene, {
      x,
      y,
      baseHp: config.baseHp,
      finalHp,
      rewardSoldiers: config.rewardSoldiers,
      rewardScore: config.rewardScore,
      width: config.width,
    });

    barrier.setScrollSpeed(this.getScrollSpeed());
    barrier.on('destroyed', (payload: unknown) => {
      this.events.emit('barrierDestroyed', payload);
    });

    this.activeBarriers.push(barrier);
    this.events.emit('barrierSpawned', barrier);

    return barrier;
  }

  private spawnPendingBarriers(): void {
    while (this.barrierSpawnIndex < this.barriersToSpawn.length) {
      const config = this.barriersToSpawn[this.barrierSpawnIndex];
      if (config.triggerDistance > this.progressDistance) {
        break;
      }

      const progress01 = this.getProgressPercent();
      const expectedArmy = this.getExpectedArmy(progress01);
      const currentArmy = this.getCurrentArmySize();
      const multiplier = this.computeDdaModifier(progress01, currentArmy, expectedArmy);

      this.lastDdaAppliedAtSpawn = {
        progress01,
        currentArmy,
        expectedArmy,
        multiplier,
      };

      this.spawnBarrierLocked(config, multiplier);
      this.barrierSpawnIndex += 1;
    }
  }

  private spawnPendingEnemies(): void {
    while (this.enemySpawnIndex < this.enemiesToSpawn.length) {
      const config = this.enemiesToSpawn[this.enemySpawnIndex];
      if (config.triggerDistance > this.progressDistance) {
        break;
      }

      this.spawnEnemy(config);
      this.enemySpawnIndex += 1;
    }
  }

  private spawnEnemy(config: SliceEnemyConfig): void {
    const x = getLaneX(config.lane, this.trackWidth, this.trackPadding);
    const y = config.spawnY ?? -40;

    const enemy: Enemy = config.type === 'elite'
      ? new EliteEnemy(this.scene, { x, y, hp: config.hp ?? 6 })
      : new Enemy(this.scene, { x, y, hp: config.hp });

    enemy.setScrollSpeed(this.getScrollSpeed());
    enemy.once('died', (points: number) => {
      this.events.emit('enemyKilled', enemy, points);
    });

    this.activeEnemies.push(enemy);
    this.events.emit('enemySpawned', enemy);
  }

  private spawnPendingGates(): void {
    while (this.gateSpawnIndex < this.gatesToSpawn.length) {
      const row = this.gatesToSpawn[this.gateSpawnIndex];
      if (row.triggerDistance > this.progressDistance) {
        break;
      }

      this.spawnGateRow(row);
      this.gateSpawnIndex += 1;
    }
  }

  private spawnGateRow(config: SliceGateRowConfig): void {
    // Spawn rows below the HUD readability band to avoid top-of-screen label overlap in captures.
    const rowY = Gate.SLICE_SPAWN_Y;
    const rowGates: Gate[] = [];

    for (const option of config.options) {
      const x = getGateLaneX(option.lane, this.trackWidth, this.trackPadding);
      const gate = new Gate(this.scene, {
        x,
        y: rowY,
        multiplier: option.multiplier,
      });
      gate.setScrollSpeed(this.getScrollSpeed());

      gate.on('activated', (multiplier: number, soldiersAdded: number) => {
        for (const otherGate of rowGates) {
          if (otherGate !== gate && otherGate.getIsActive()) {
            otherGate.deactivate();
          }
        }
        this.events.emit('gateActivated', gate, multiplier, soldiersAdded);
      });

      rowGates.push(gate);
      this.activeGates.push(gate);
    }

    this.events.emit('gateRowSpawned', rowGates);
  }

  private updateActiveBarriers(delta: number): void {
    updateAndCompactEntities(
      this.activeBarriers,
      delta,
      (barrier) => barrier.getIsActive() && barrier.active
    );
  }

  private updateActiveEnemies(delta: number): void {
    updateAndCompactEntities(
      this.activeEnemies,
      delta,
      (enemy) => enemy.getIsAlive() && enemy.active
    );
  }

  private updateActiveGates(delta: number): void {
    updateAndCompactEntities(
      this.activeGates,
      delta,
      (gate) => gate.getIsActive() && gate.active
    );
  }

  private getExpectedArmy(progress01: number): number {
    const clamped = Phaser.Math.Clamp(progress01, 0, 1);
    return 1 + clamped * 24;
  }

  getEvents(): Phaser.Events.EventEmitter {
    return this.events;
  }

  getProgressDistance(): number {
    return this.progressDistance;
  }

  getProgressPercent(): number {
    if (this.levelConfig.trackLength <= 0) return 0;
    return Phaser.Math.Clamp(this.progressDistance / this.levelConfig.trackLength, 0, 1);
  }

  getTrackLength(): number {
    return this.levelConfig.trackLength;
  }

  getIsLevelComplete(): boolean {
    return this.isLevelComplete;
  }

  getActiveBarriers(): Barrier[] {
    return this.activeBarriers;
  }

  getActiveEnemies(): Enemy[] {
    return this.activeEnemies;
  }

  getActiveGates(): Gate[] {
    return this.activeGates;
  }

  getLastDdaAppliedAtSpawn(): DdaSnapshot | null {
    return this.lastDdaAppliedAtSpawn;
  }

  destroy(): void {
    this.events.removeAllListeners();

    for (const barrier of this.activeBarriers) {
      barrier.destroy();
    }
    this.activeBarriers.length = 0;

    for (const enemy of this.activeEnemies) {
      enemy.destroy();
    }
    this.activeEnemies.length = 0;

    for (const gate of this.activeGates) {
      gate.destroy();
    }
    this.activeGates.length = 0;
  }
}
